export interface MediaFile {
  id: number;
  mediaType: number;
  url: string;
}

export interface WithPagination<T> {
  pagination: {
    currentPage: number;
    lastPage: number;
    itemsPerPage: number;
    total: number;
  };
  data: T[];
}

export interface Permission {
  id: number;
  name: string;
}

export interface Country {
  id: number;
  name: string;
  // ISO-3166-1 alpha-2 (e.g. "RO", "GB"). Comes from countries.iso2 on
  // the backend via CountryResource — exposed so admin forms can pass
  // it through to Google Places' `components=country:xx` filter.
  code: string;
  latitude: number;
  longitude: number;
}

export interface County {
  id: number;
  countryId: number;
  name: string;
  code: string;
  latitude: number;
  longitude: number;
}

export interface City {
  countryId: number;
  countyId: number;
  id: number;
  latitude: string;
  longitude: string;
  name: string;
  code: string;
}

export interface ServiceInterface {
  id: number;
  name: string;
  category: {
    id: number;
    name: string;
  }
}

// Mirrors sporapi ContentTopicEnum. Binds a category to the bot worker's news
// feeds for that topic, so the worker routes sourced content by topic instead of
// by a hardcoded category id. Adding a case requires a matching feed in the
// worker's registry.py.
export enum ContentTopic {
  Sport = "sport",
  Monden = "monden",
  Global = "global",
  Actualitate = "actualitate",
}

export interface CategoryInterface {
  id: number;
  name: string;
  // null = the category takes no sourced bot content.
  topic: ContentTopic | null;
  services: ServiceInterface[];
}

export interface NotificationInterface {
  id: string;
  data: {
    userId?: number;
    title: string;
    reportId?: number;
    post?: number
    created_at: string;
  };
  type: NotificationsTypes;
  title: string;
  readAt: string;
  createdAt: string;
}

export interface PlatformSettings {
  id: number;
  postExpireDays: number;
  paidPostPricePerDay: number;
  verifiedBadgePricePerMonth: number;
  // Daily-quota knobs — when dailyQuotasEnabled is false the limits
  // are ignored platform-wide; when true the four numeric caps apply
  // to non-bot accounts. Bot accounts (users.is_bot) bypass either way.
  dailyQuotasEnabled: boolean;
  maxPostsPerDay: number;
  maxVideoPostsPerDay: number;
  maxCommentsPerDay: number;
  maxVideoCommentsPerDay: number;
  // Maximum upload duration (in seconds) for any user-supplied video —
  // post media, comment media, or quote-repost media. Default 60.
  maxVideoDurationSeconds: number;
  // Maximum recording length (in seconds) for a single live event session.
  // Separate from maxVideoDurationSeconds because live events legitimately
  // need longer cuts (a half-match, a full set, a press conference).
  // Default 1800 (30 minutes).
  maxEventLiveDurationSeconds: number;
  // Maximum radius (in km) an admin can set on a live event. Drives the
  // ceiling on the event geofence slider + validation. Default 3000.
  eventMaxRadiusKm: number;
  createdAt: string;
  updatedAt: string;
}

export enum NotificationsTypes {
  NEW_CONTACT_FORM_SUBMITED = "new-contact-form-submited",
  NEW_POST_CREATED = "new-post-created",
  NEW_REPORT_CREATED = "new-report-created",
  NEW_USER_REGISTERED = "new-user-registered",
  UPCOMING_POST = "upcoming-post",
  USER_UPGRADE = "user-upgrade",
}

/**
 * Keys of the server-driven app-update gate, stored in the generic
 * `app_settings` key/value table. Edited by their storage key rather
 * than a camel-cased alias so the dashboard shows exactly what an
 * operator would grep for in the database.
 *
 * An empty value switches that level off; both version levels empty
 * means no client ever sees a prompt, which is the default state.
 */
export type AppUpdateSettingKey =
  | "app.min_version.ios"
  | "app.min_version.android"
  | "app.latest_version.ios"
  | "app.latest_version.android"
  | "app.store_url.ios"
  | "app.store_url.android";

export type AppUpdateSettings = Record<AppUpdateSettingKey, string>;

export interface AppSettingsResponse {
  settings: AppUpdateSettings;
}
